import Image from 'next/image';

import { Box, Flex, Heading, SimpleGrid, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { Glyph } from '~/components/glyph';
import { Markdown } from '~/components/markdown';

import { AboutUsTemplate } from '../model';

export function MissionSection({
  missionTitleText,
  missionStats,
}: {
  missionTitleText: AboutUsTemplate['missionTitleText'];
  missionStats: AboutUsTemplate['missionStats'];
}) {
  return (
    <Box as='section' py={{ base: '40px', md: '80px' }}>
      <ContentContainer>
        <Flex alignItems='center' direction={{ base: 'column', lg: 'row' }} gap='32px'>
          <Flex flexDirection='column' gap='16px'>
            <Heading as='h3' size='h2'>
              {missionTitleText.title}
            </Heading>
            <Markdown markdownText={missionTitleText.text} />
          </Flex>

          <Box
            flexShrink={0}
            paddingX={{ base: '32px', lg: 'unset' }}
            position='relative'
            width={{ base: '100%', lg: 'unset' }}
          >
            <Box display={{ base: 'none', lg: 'block' }} height={512} left={0} width={640}>
              <Image alt='' src='/images/backgrounds/floating-shapes.png' fill />
            </Box>
            <SimpleGrid
              columns={{ base: 1, lg: 2 }}
              position={{ base: 'relative', lg: 'absolute' }}
              right={{ base: 'unset', lg: '32px' }}
              spacing='32px'
              top={{ base: 'unset', lg: '50%' }}
              transform={{ base: 'unset', lg: 'translateY(-50%)' }}
            >
              {missionStats.map((missionStat, index) => (
                <MissionStat
                  key={index}
                  accentColor={index % 2 === 0 ? 'accent.purple' : 'accent.lime'}
                  borderRadius={borderRadiusMap.get(index)}
                  missionStat={missionStat}
                />
              ))}
            </SimpleGrid>
          </Box>
        </Flex>
      </ContentContainer>
    </Box>
  );
}

const borderRadiusMap = new Map();
borderRadiusMap.set(0, '4px 16px 4px 16px');
borderRadiusMap.set(1, '16px 4px 16px 4px');
borderRadiusMap.set(2, '16px 4px 16px 4px');
borderRadiusMap.set(3, '4px 16px 4px 16px');

function MissionCard({ glyphName, text }: { glyphName: string; text: string }) {
  return (
    <Flex
      alignItems='center'
      bg='white'
      borderRadius='base'
      flex={1}
      flexDirection='column'
      gap='16px'
      px='32px'
      py='40px'
    >
      <Glyph name={glyphName} />
      <Text size='label'>{text}</Text>
    </Flex>
  );
}

function MissionStat({
  missionStat,
  accentColor,
  borderRadius,
}: {
  missionStat: AboutUsTemplate['missionStats'][number];
  accentColor: 'accent.purple' | 'accent.lime';
  borderRadius: '4px 16px 4px 16px' | '16px 4px 16px 4px';
}) {
  return (
    <Flex
      alignItems='center'
      bgGradient='linear(to-b, #1A1E62, #070933)'
      borderRadius={borderRadius}
      flexDirection='column'
      p='32px'
      textAlign='center'
    >
      <Heading as='div' color='white' size='marketing-5xl'>
        {missionStat.title}
      </Heading>
      <Text color={accentColor} maxWidth='200px'>
        {missionStat.text}
      </Text>
    </Flex>
  );
}
